import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

type IssueId = Id<'newsletterIssues'>

const field =
  'w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
const pill =
  'cursor-pointer rounded-full px-3 py-1 text-xs font-semibold transition-opacity disabled:cursor-default disabled:opacity-40'

const errMsg = (e: unknown, fallback: string) =>
  e instanceof ConvexError && typeof e.data === 'string' ? e.data : fallback

const empty = { slug: '', subject: '', previewText: '', html: '' }

// Monthly newsletter: save an issue, test it on yourself, then send to subscribers.
export function AdminNewsletter() {
  const issues = useQuery(api.newsletter.listIssues)
  const subscribers = useQuery(api.newsletter.subscriberCount)
  const save = useMutation(api.newsletter.saveIssue)
  const sendTest = useMutation(api.newsletter.sendTest)
  const [form, setForm] = useState(empty)
  const [editing, setEditing] = useState<IssueId | null>(null)
  const [composing, setComposing] = useState(false)
  const loaded = useQuery(api.newsletter.getIssue, editing ? { issueId: editing } : 'skip')
  const [confirming, setConfirming] = useState<{ id: IssueId; slug: string } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (loaded)
      setForm({
        slug: loaded.slug,
        subject: loaded.subject,
        previewText: loaded.previewText,
        html: loaded.html,
      })
  }, [loaded])

  if (!issues) return null

  async function onSave() {
    setBusy(true)
    try {
      setEditing(await save(form))
      toast.success('Saved draft')
    } catch (e) {
      toast.error(errMsg(e, 'Could not save'))
    } finally {
      setBusy(false)
    }
  }

  async function onTest(issueId: IssueId) {
    try {
      toast.success(`Test sent to ${await sendTest({ issueId })}`)
    } catch (e) {
      toast.error(errMsg(e, 'Could not send test'))
    }
  }

  function onFile(file: File | undefined) {
    if (!file) return
    void file.text().then((html) => {
      const slug = file.name.replace(/\.html?$/i, '').toLowerCase()
      setForm((f) => ({ ...f, html, slug: f.slug || slug }))
    })
  }

  const set = (k: keyof typeof empty) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center justify-between text-xs font-semibold uppercase tracking-widest text-foreground/40">
        Newsletter · {subscribers ?? '…'} subscribers
        {!composing && !editing && (
          <button
            type="button"
            onClick={() => setComposing(true)}
            className={cn(
              pill,
              'bg-primary font-bold normal-case tracking-normal text-primary-foreground',
            )}
          >
            New issue
          </button>
        )}
      </h2>

      {(composing || editing) && (
        <div className="flex flex-col gap-2 rounded-xl border border-border/60 bg-card/40 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">
              {editing ? 'Edit issue' : 'New issue'}
            </span>
            <button
              type="button"
              onClick={() => {
                setEditing(null)
                setComposing(false)
                setForm(empty)
              }}
              className="cursor-pointer text-xs text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
          <input
            className={field}
            placeholder="slug (matches goaltree.ai/blog/<slug>)"
            value={form.slug}
            onChange={set('slug')}
            disabled={!!editing}
          />
          <input
            className={field}
            placeholder="Subject"
            value={form.subject}
            onChange={set('subject')}
          />
          <input
            className={field}
            placeholder="Preview text"
            value={form.previewText}
            onChange={set('previewText')}
          />
          <textarea
            className={cn(field, 'h-40 font-mono text-xs')}
            placeholder="Paste HTML. Tags: {{first_name}} {{unsubscribe_url}} {{view_in_browser_url}}"
            value={form.html}
            onChange={set('html')}
          />
          <div className="flex flex-wrap items-center gap-2">
            <label className={cn(pill, 'bg-muted text-muted-foreground hover:text-foreground')}>
              Pick .html file
              <input
                type="file"
                accept=".html,.htm,text/html"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
            </label>
            <span className="text-[11px] text-muted-foreground">
              {(form.html.length / 1000).toFixed(1)}KB
            </span>
            <button
              type="button"
              disabled={busy || !form.slug || !form.subject || !form.html}
              onClick={onSave}
              className={cn(pill, 'ml-auto bg-primary font-bold text-primary-foreground')}
            >
              Save draft
            </button>
          </div>
        </div>
      )}

      {issues.length === 0 && <p className="text-sm text-muted-foreground">No issues yet.</p>}
      {issues.map((i) => (
        <div
          key={i._id}
          className="flex flex-col gap-2 rounded-xl border border-border/60 bg-card/40 p-3"
        >
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
              {i.subject}
            </span>
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                i.status === 'sent'
                  ? 'bg-primary/15 text-primary-text'
                  : i.status === 'sending'
                    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              {i.status}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {i.slug} · {(i.htmlBytes / 1000).toFixed(1)}KB
            {i.testSentAt && ` · tested ${new Date(i.testSentAt).toLocaleString()}`}
            {i.status !== 'draft' &&
              ` · ${i.recipients ?? 0}${i.status === 'sending' && subscribers ? ` / ~${subscribers}` : ''} sent`}
            {i.sentAt && ` · ${new Date(i.sentAt).toLocaleString()}`}
          </p>
          {i.status === 'draft' && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setEditing(i._id)}
                className={cn(pill, 'bg-muted text-muted-foreground hover:text-foreground')}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void onTest(i._id)}
                className={cn(pill, 'bg-muted text-foreground')}
              >
                Send test to me
              </button>
              <button
                type="button"
                disabled={!i.testSentAt}
                title={i.testSentAt ? undefined : 'Send yourself a test first'}
                onClick={() => setConfirming({ id: i._id, slug: i.slug })}
                className={cn(pill, 'bg-primary font-bold text-primary-foreground')}
              >
                Send to subscribers
              </button>
            </div>
          )}
        </div>
      ))}

      <ConfirmSend
        target={confirming}
        subscribers={subscribers ?? null}
        onClose={() => setConfirming(null)}
      />
    </section>
  )
}

function ConfirmSend({
  target,
  subscribers,
  onClose,
}: {
  target: { id: IssueId; slug: string } | null
  subscribers: number | null
  onClose: () => void
}) {
  const send = useMutation(api.newsletter.sendToSubscribers)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => setTyped(''), [target])

  async function onSend() {
    if (!target) return
    setBusy(true)
    try {
      await send({ issueId: target.id, confirm: typed })
      toast.success('Sending started')
      onClose()
    } catch (e) {
      toast.error(errMsg(e, 'Could not start sending'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-w-md flex-col gap-3 p-5">
        <DialogTitle className="text-lg font-extrabold tracking-tight">
          Send to {subscribers ?? '…'} subscribers?
        </DialogTitle>
        <p className="text-sm text-muted-foreground">
          This can't be undone. Type <b className="font-mono text-foreground">{target?.slug}</b> to
          confirm.
        </p>
        <input
          className={field}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          autoComplete="off"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className={cn(pill, 'bg-muted text-muted-foreground')}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || typed.trim() !== target?.slug}
            onClick={onSend}
            className={cn(pill, 'bg-primary font-bold text-primary-foreground')}
          >
            Send now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
